import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  FileText,
  ListChecks,
  Clock,
  AlertCircle,
  MessageSquare,
  TrendingUp,
  Ship,
  Database,
  Newspaper,
  Loader2,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { getCasExecutiveSummary, syncCasNews } from '../services/api';

const DATASET_LABELS = {
  price: { label: 'Price', icon: TrendingUp, color: 'text-emerald-600 dark:text-emerald-400' },
  trade: { label: 'Trade', icon: Ship, color: 'text-blue-600 dark:text-blue-400' },
  reserve: { label: 'Reserve', icon: Database, color: 'text-amber-600 dark:text-amber-400' },
  news: { label: 'News / NLP', icon: Newspaper, color: 'text-violet-600 dark:text-violet-400' },
};

function UrgencyBadge({ urgency }) {
  const config = {
    low: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-200 dark:border-green-800',
    medium: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-800',
    high: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 border-orange-200 dark:border-orange-800',
    critical: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800',
  };
  const style = config[urgency?.toLowerCase()] || config.medium;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium border capitalize ${style}`}>
      {urgency || '—'}
    </span>
  );
}

function parseRecommendedActions(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(String);
  const s = String(value).trim();
  if (!s) return [];
  return s.split(/\s*;\s*/).filter(Boolean);
}

/** Show only the LLM narrative; if the value is the full JSON blob, extract llm_analysis. */
function getDetailedAnalysisText(value) {
  if (value == null || value === '') return '';
  const s = typeof value === 'string' ? value.trim() : String(value).trim();
  if (!s) return '';
  const stripped = s.replace(/^json\s*/i, '').trim();
  if ((stripped.startsWith('{') && stripped.includes('"llm_analysis"')) || stripped.startsWith('{"executive_summary"')) {
    try {
      const obj = JSON.parse(stripped);
      const analysis = obj.llm_analysis;
      return typeof analysis === 'string' ? analysis.trim() : s;
    } catch (_) {
      return s;
    }
  }
  return s;
}

const CHIP_OPTIONS = [
  { id: 'all', label: 'All', icon: FileText },
  { id: 'price', label: 'Price', icon: TrendingUp },
  { id: 'trade', label: 'Trade', icon: Ship },
  { id: 'reserve', label: 'Reserve', icon: Database },
  { id: 'news', label: 'News / NLP', icon: Newspaper },
];

const ExecutiveSummaryPage = () => {
  const [data, setData] = useState({ items: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedChip, setSelectedChip] = useState('all');
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);

  const fetchData = () => {
    setLoading(true);
    setError(null);
    setSyncMessage(null);
    getCasExecutiveSummary(100)
      .then((res) => {
        setData(res?.items ? { items: res.items, count: res.count } : { items: [], count: 0 });
      })
      .catch((err) => setError(err?.message || 'Failed to load executive summary'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSyncNews = () => {
    setSyncing(true);
    setSyncMessage(null);
    syncCasNews()
      .then((res) => {
        setSyncMessage(res?.message || 'Sync started. Refresh in a few minutes.');
      })
      .catch((err) => setSyncMessage(err?.message || 'Sync failed.'))
      .finally(() => setSyncing(false));
  };

  const byType = useMemo(() => {
    const map = { price: [], trade: [], reserve: [], news: [] };
    (data.items || []).forEach((item) => {
      const t = (item.dataset_type || '').toLowerCase();
      if (map[t]) map[t].push(item);
      else map.news.push(item);
    });
    return map;
  }, [data.items]);

  const actionsByType = useMemo(() => {
    const map = { price: [], trade: [], reserve: [], news: [] };
    const seenByType = { price: new Set(), trade: new Set(), reserve: new Set(), news: new Set() };
    (data.items || []).forEach((item) => {
      const t = ((item.dataset_type || '').toLowerCase()) || 'news';
      const key = map[t] ? t : 'news';
      const seen = seenByType[key];
      parseRecommendedActions(item.recommended_action).forEach((action) => {
        const a = action.trim();
        const k = a.toLowerCase();
        if (a && !seen.has(k)) {
          seen.add(k);
          map[key].push(a);
        }
      });
    });
    return map;
  }, [data.items]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[40vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600 dark:text-primary-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center gap-2 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <FileText className="h-7 w-7 text-primary-600 dark:text-primary-400" />
          Executive Summary
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Price changes, trade & reserve shifts, and news-driven alerts with recommended actions — all in one place
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {CHIP_OPTIONS.map(({ id, label, icon: Icon }) => {
          const isActive = selectedChip === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setSelectedChip(id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                isActive
                  ? 'bg-primary-600 dark:bg-primary-500 text-white border-primary-600 dark:border-primary-500 shadow-sm'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          );
        })}
      </div>

      {(['price', 'trade', 'reserve', 'news'].some((t) => (actionsByType[t] || []).length > 0)) &&
       (selectedChip === 'all' || (actionsByType[selectedChip] || []).length > 0) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-gray-900 dark:text-gray-100">
              <ListChecks className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              Recommended actions by category
            </CardTitle>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Segregated by Price, Trade, Reserve, and News / NLP
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            {(selectedChip === 'all' ? ['price', 'trade', 'reserve', 'news'] : [selectedChip]).map((datasetType) => {
              const actions = actionsByType[datasetType] || [];
              if (actions.length === 0) return null;
              const { label, icon: Icon, color } = DATASET_LABELS[datasetType] || DATASET_LABELS.news;
              return (
                <div key={datasetType}>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-2">
                    <Icon className={`h-4 w-4 ${color}`} />
                    {label}
                  </h3>
                  <ul className="space-y-1.5 list-none pl-0 ml-6">
                    {actions.map((action, i) => (
                      <li key={`${datasetType}-${i}`} className="flex gap-2 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 flex items-center justify-center text-xs font-medium">
                          {i + 1}
                        </span>
                        <span>{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {(selectedChip === 'all' ? ['price', 'trade', 'reserve', 'news'] : [selectedChip]).map((datasetType) => {
        const items = byType[datasetType] || [];
        if (items.length === 0) return null;
        const { label, icon: Icon, color } = DATASET_LABELS[datasetType] || DATASET_LABELS.news;
        return (
          <div key={datasetType}>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
              <Icon className={`h-5 w-5 ${color}`} />
              {label}
            </h2>
            <div className="space-y-4">
              {items.map((item) => {
                const actions = parseRecommendedActions(item.recommended_action);
                return (
                  <Card key={item.id || item.processed_at}>
                    <CardHeader className="pb-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <CardTitle className="text-base text-gray-900 dark:text-gray-100">
                          {item.title || 'Alert'}
                        </CardTitle>
                        {item.commodity && (
                          <span className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                            {item.commodity}
                          </span>
                        )}
                        {item.country && (
                          <span className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                            {item.country}
                          </span>
                        )}
                        <UrgencyBadge urgency={item.action_urgency} />
                      </div>
                      {item.short_summary && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {item.short_summary}
                        </p>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {item.executive_summary && (
                        <div>
                          <p className="text-base font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
                            <MessageSquare className="h-4 w-4" /> Summary
                          </p>
                          <p className="text-gray-700 dark:text-gray-300 text-base leading-relaxed">
                            {item.executive_summary}
                          </p>
                        </div>
                      )}
                      {actions.length > 0 && (
                        <div>
                          <p className="text-base font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
                            <ListChecks className="h-4 w-4" /> What should we do?
                          </p>
                          <ul className="space-y-1.5 list-none pl-0">
                            {actions.map((a, i) => (
                              <li key={i} className="flex gap-2 text-gray-700 dark:text-gray-300 text-base leading-relaxed">
                                <span className="text-primary-600 dark:text-primary-400">•</span>
                                <span>{a}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-4 text-sm">
                        {item.action_deadline ? (
                          <span className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                            <Clock className="h-3 w-3" />
                            {new Date(item.action_deadline).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-gray-500 dark:text-gray-500 italic">
                            <Clock className="h-3 w-3" /> No deadline
                          </span>
                        )}
                        {item.severity_score != null && (
                          <span className="text-gray-600 dark:text-gray-400">
                            Severity: {(item.severity_score * 100).toFixed(0)}%
                          </span>
                        )}
                      </div>
                      {getDetailedAnalysisText(item.llm_analysis) && (
                        <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                          <p className="text-base font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
                            <FileText className="h-4 w-4" /> Detailed analysis
                          </p>
                          <p className="text-gray-700 dark:text-gray-300 text-base leading-relaxed whitespace-pre-wrap">
                            {getDetailedAnalysisText(item.llm_analysis)}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}

      {data.count === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              No CAS alerts yet. Run the CAS pipeline to ingest price, trade, reserve, and news events.
            </p>
            <div className="flex flex-col items-center gap-3">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Or sync news from the database (with OpenAI enrichment) to populate News/NLP.
              </p>
              <button
                type="button"
                onClick={handleSyncNews}
                disabled={syncing}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                {syncing ? 'Starting sync…' : 'Sync news to CAS'}
              </button>
              {syncMessage && (
                <div className="flex flex-col items-center gap-2">
                  <p className="text-sm text-gray-700 dark:text-gray-200">{syncMessage}</p>
                  <button type="button" onClick={fetchData} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-sm">
                    <RefreshCw className="h-3 w-3" /> Refresh
                  </button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {data.count > 0 && selectedChip !== 'all' && (byType[selectedChip] || []).length === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              No {DATASET_LABELS[selectedChip]?.label || selectedChip} alerts or recommendations. Select another category or All.
            </p>
            {selectedChip === 'news' && (
              <div className="flex flex-col items-center gap-3">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Sync news from the database with OpenAI enrichment to show News/NLP here.
                </p>
                <button
                  type="button"
                  onClick={handleSyncNews}
                  disabled={syncing}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  {syncing ? 'Starting sync…' : 'Sync news to CAS'}
                </button>
                {syncMessage && (
                  <div className="flex flex-col items-center gap-2">
                    <p className="text-sm text-gray-700 dark:text-gray-200">{syncMessage}</p>
                    <button
                      type="button"
                      onClick={fetchData}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-sm"
                    >
                      <RefreshCw className="h-3 w-3" /> Refresh
                    </button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ExecutiveSummaryPage;
